// Small longest-common-subsequence diff for replay change highlighting (code by line,
// explanations by word). Inputs are a student's own code or prose, so they are small; past
// MAX_CELLS the diff gives up and reports everything as replaced rather than stall the page.
const MAX_CELLS = 2000000

// -> [{ type: 'same' | 'add' | 'remove', value }] turning `before` into `after`. The shared
// start and end are set aside first, so a small edit to a long text stays small.
export function diffTokens (before, after) {
  let start = 0
  while (start < before.length && start < after.length && before[start] === after[start]) start++
  let end = 0
  while (end < before.length - start && end < after.length - start &&
    before[before.length - 1 - end] === after[after.length - 1 - end]) end++
  const same = (tokens) => tokens.map(value => ({ type: 'same', value }))
  return [
    ...same(after.slice(0, start)),
    ...diffMiddle(before.slice(start, before.length - end), after.slice(start, after.length - end)),
    ...same(after.slice(after.length - end)),
  ]
}

function diffMiddle (before, after) {
  const n = before.length
  const m = after.length
  if (n * m > MAX_CELLS) {
    return [...before.map(value => ({ type: 'remove', value })), ...after.map(value => ({ type: 'add', value }))]
  }
  // lengths[i][j]: LCS length of before[i..] and after[j..].
  const lengths = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lengths[i][j] = before[i] === after[j] ? lengths[i + 1][j + 1] + 1 : Math.max(lengths[i + 1][j], lengths[i][j + 1])
    }
  }
  const ops = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      ops.push({ type: 'same', value: after[j] })
      i++
      j++
    } else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
      ops.push({ type: 'remove', value: before[i++] })
    } else {
      ops.push({ type: 'add', value: after[j++] })
    }
  }
  while (i < n) ops.push({ type: 'remove', value: before[i++] })
  while (j < m) ops.push({ type: 'add', value: after[j++] })
  return ops
}

// 1-based line numbers in `after` that were added or changed since `before`.
export function changedLineNumbers (before, after) {
  const lines = []
  let line = 0
  for (const op of diffTokens(String(before).split('\n'), String(after).split('\n'))) {
    if (op.type === 'remove') continue
    line++
    if (op.type === 'add') lines.push(line)
  }
  return lines
}

// Word-level diff that keeps the whitespace, so joining every non-removed value gives `after`.
export function diffWords (before, after) {
  const tokenize = (text) => String(text).split(/(\s+)/).filter(Boolean)
  return diffTokens(tokenize(before), tokenize(after))
}
